import React, { lazy, Suspense, useEffect } from 'react';
import { connect } from 'react-redux';
import type { ConnectedProps } from 'react-redux';
import ErrorBoundary from 'src/error/components/ErrorBoundary';
import { getIsEffectiveTypeFastOrNotDefined } from 'src/utils/networkInformation';
import styled from 'styled-components';
import { TEST_ID_PRICE_CHART_CONTAINER } from './constants';
import { fetchPriceChart as fetchPriceChartAction } from '../../store/priceChart/actions';
import {
  isEmptyResponsePriceChartSelector,
  isShowingPriceChartSelector,
  priceChartIsFetchingSelector,
} from '../../store/priceChart/selectors';
import PriceChartPreview from './components/PriceChartPreview';

const PriceChart = lazy(() => import(/* webpackChunkName: "PriceChart" */ '.'));

export const LazyPriceChartComponent = (
  props: PropsFromRedux & { className?: string },
): JSX.Element | null => {
  const {
    fetchPriceChart,
    isLoadingGetGroupHotels,
    isFetching,
    isResponseEmpty,
    className,
  } = props;

  const isNetworkFast = getIsEffectiveTypeFastOrNotDefined();

  useEffect(() => {
    if (isNetworkFast && !isLoadingGetGroupHotels) {
      fetchPriceChart();
    }
  }, [fetchPriceChart, isLoadingGetGroupHotels, isNetworkFast]);

  if (
    !isNetworkFast ||
    (isResponseEmpty && !isFetching && !isLoadingGetGroupHotels)
  ) {
    return null;
  }

  return (
    <PriceChartContainer
      data-testid={TEST_ID_PRICE_CHART_CONTAINER}
      className={className}
    >
      {!isLoadingGetGroupHotels && !isFetching ? (
        <ErrorBoundary showDialog={false}>
          <Suspense fallback={<PriceChartPreview />}>
            <PriceChart />
          </Suspense>
        </ErrorBoundary>
      ) : (
        <PriceChartPreview />
      )}
    </PriceChartContainer>
  );
};

const PriceChartContainer = styled.div`
  height: 96px;
`;

const mapStateToProps = (state) => ({
  isLoadingGetGroupHotels: isShowingPriceChartSelector(state),
  isFetching: priceChartIsFetchingSelector(state),
  isResponseEmpty: isEmptyResponsePriceChartSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  fetchPriceChart: () => dispatch(fetchPriceChartAction()),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const LazyPriceChart = connector(LazyPriceChartComponent);

export default LazyPriceChart;
